'use client';
import React, { createContext, useContext, startTransition } from 'react';
import { SessionContext } from '@benzinga/session-context';
import {
  generateOverviewWatchlist,
  Watchlist,
  WatchlistManager,
  WatchlistManagerEvent,
} from '@benzinga/watchlist-manager';

export interface WatchlistContextType {
  addTickerModal: { show: boolean; ticker: string | null; isLoading: boolean };
  deleteModal: { show: boolean; watchlist: Watchlist | null; isLoading: boolean };
  deleteTickerModal: { show: boolean; ticker: string | null; isLoading: boolean };
  editModal: { show: boolean; watchlist: Watchlist | null };
  isLoadingWatchlists: boolean;
  overviewWatchlist: Watchlist | null;
  selectedWatchlist: Watchlist | null;
  setAddTickerModal: (modal: { show: boolean; ticker: string | null; isLoading: boolean }) => void;
  setDeleteModal: (modal: { show: boolean; watchlist: Watchlist | null; isLoading: boolean }) => void;
  setDeleteTickerModal: (modal: { show: boolean; ticker: string | null; isLoading: boolean }) => void;
  setEditModal: (modal: { show: boolean; watchlist: Watchlist | null }) => void;
  setSelectedWatchlist: (watchlist: Watchlist) => void;
  setShowCreateModal: (show: boolean) => void;
  setShowNotificationsModal: (modal: { show: boolean; watchlist: Watchlist | null }) => void;
  setShowYodleeModal: (show: boolean) => void;
  showCreateModal: boolean;
  showNotificationsModal: { show: boolean; watchlist: Watchlist | null };
  showYodleeModal: boolean;
  watchlists: Watchlist[];
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export interface WatchlistProviderProps {
  children: React.ReactNode;
}

export const WatchlistProvider: React.FC<WatchlistProviderProps> = ({ children }) => {
  const [selectedWatchlist, setSelectedWatchlistInternal] = React.useState<Watchlist | null>(null);
  const [watchlists, setWatchlists] = React.useState<Watchlist[]>([]);
  const [overviewWatchlist, setOverviewWatchlist] = React.useState<Watchlist | null>(null);
  const [isLoadingWatchlists, setIsLoadingWatchlists] = React.useState(true);

  // Optimize setSelectedWatchlist to prevent unnecessary updates when watchlistId is the same
  const setSelectedWatchlist = React.useCallback((watchlist: Watchlist) => {
    setSelectedWatchlistInternal(prev => {
      // Only update if watchlistId actually changed
      if (prev?.watchlistId === watchlist.watchlistId && prev.symbols.length === watchlist.symbols.length) {
        return prev;
      }
      return watchlist;
    });
  }, []);

  // modals management
  const [showYodleeModal, setShowYodleeModal] = React.useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [showNotificationsModal, setShowNotificationsModal] = React.useState<
    WatchlistContextType['showNotificationsModal']
  >({
    show: false,
    watchlist: null,
  });
  const [deleteModal, setDeleteModal] = React.useState<WatchlistContextType['deleteModal']>({
    isLoading: false,
    show: false,
    watchlist: null,
  });
  const [editModal, setEditModal] = React.useState<WatchlistContextType['editModal']>({
    show: false,
    watchlist: null,
  });
  // for Overview watchlist
  const [addTickerModal, setAddTickerModal] = React.useState<WatchlistContextType['addTickerModal']>({
    isLoading: false,
    show: false,
    ticker: null,
  });
  const [deleteTickerModal, setDeleteTickerModal] = React.useState<WatchlistContextType['deleteTickerModal']>({
    isLoading: false,
    show: false,
    ticker: null,
  });

  const session = React.useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);

  React.useEffect(() => {
    const init = async () => {
      try {
        const watchlists = await watchlistManager.getWatchlists();
        setWatchlists(watchlists?.ok ?? []);

        const overview = generateOverviewWatchlist(watchlists?.ok ?? []);
        setOverviewWatchlist(overview);
        setSelectedWatchlistInternal(overview ?? watchlists?.ok?.[0] ?? null);
      } finally {
        setIsLoadingWatchlists(false);
      }
    };
    startTransition(() => {
      init();
    });
  }, [watchlistManager]);

  // add listener to watchlist manager for watchlist updates
  React.useEffect(() => {
    const subscription = watchlistManager.subscribe((event: WatchlistManagerEvent) => {
      if (event.type === 'watchlist:updated_watchlists') {
        setWatchlists(event.watchlists);
        const overview = generateOverviewWatchlist(event.watchlists);
        setOverviewWatchlist(overview);
        setSelectedWatchlistInternal(prev => {
          if (prev?.watchlistId === 'overview') {
            return overview;
          }
          return (
            event.watchlists.find(watchlist => watchlist.watchlistId === prev?.watchlistId) ??
            overview ??
            event.watchlists[0] ??
            null
          );
        });
        if (showNotificationsModal.show && showNotificationsModal.watchlist !== null) {
          const modalWatchlist = event.watchlists.find(
            watchlist => watchlist.watchlistId === showNotificationsModal.watchlist?.watchlistId,
          );
          if (modalWatchlist) {
            setShowNotificationsModal({ show: true, watchlist: modalWatchlist });
          }
        }
        if (editModal.show && editModal.watchlist !== null) {
          const modalWatchlist = event.watchlists.find(
            watchlist => watchlist.watchlistId === editModal.watchlist?.watchlistId,
          );
          setEditModal({ show: Boolean(modalWatchlist), watchlist: modalWatchlist ?? null });
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [
    editModal.show,
    editModal.watchlist,
    showNotificationsModal.show,
    showNotificationsModal.watchlist,
    watchlistManager,
  ]);

  const value: WatchlistContextType = {
    addTickerModal,
    deleteModal,
    deleteTickerModal,
    editModal,
    isLoadingWatchlists,
    overviewWatchlist,
    selectedWatchlist,
    setAddTickerModal,
    setDeleteModal,
    setDeleteTickerModal,
    setEditModal,
    setSelectedWatchlist,
    setShowCreateModal,
    setShowNotificationsModal,
    setShowYodleeModal,
    showCreateModal,
    showNotificationsModal,
    showYodleeModal,
    watchlists,
  };

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
};

export const useWatchlistContext = (): WatchlistContextType => {
  const context = useContext(WatchlistContext);
  if (context === undefined) {
    throw new Error('useWatchlistContext must be used within a WatchlistProvider');
  }
  return context;
};
